import type { PixelClient } from "../client.js";
import type { ActiveTimeTracker } from "./active-time.js";
import { pageContext } from "./page-context.js";

const NAVIGATION_SUPPRESSION_MS = 5_000;

/** Best-effort page-leave event with total + active dwell time. */
export function attachPageLeave(client: PixelClient, activeTime: ActiveTimeTracker): () => void {
  let sent = false;
  let expectedNavigation = false;
  let navigationTimer: number | null = null;

  const clearExpectedNavigation = () => {
    expectedNavigation = false;
    if (navigationTimer !== null) {
      window.clearTimeout(navigationTimer);
      navigationTimer = null;
    }
  };

  const markExpectedNavigation = () => {
    expectedNavigation = true;
    if (navigationTimer !== null) window.clearTimeout(navigationTimer);
    navigationTimer = window.setTimeout(clearExpectedNavigation, NAVIGATION_SUPPRESSION_MS);
  };

  const flush = () => {
    if (sent || expectedNavigation) return;
    sent = true;
    const { totalDwellSec, activeDwellSec } = activeTime.snapshot();
    void client.track(
      "session_end",
      {
        path: location.pathname,
        total_dwell_sec: totalDwellSec,
        active_dwell_sec: activeDwellSec,
      },
      pageContext(),
    );
  };

  const onHide = () => {
    if (document.visibilityState === "hidden") flush();
  };

  const onClick = (event: MouseEvent) => {
    if (isDocumentNavigationClick(event)) markExpectedNavigation();
  };

  const onSubmit = (event: SubmitEvent) => {
    if (isDocumentNavigationSubmit(event)) markExpectedNavigation();
  };

  document.addEventListener("click", onClick, true);
  document.addEventListener("submit", onSubmit, true);
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", flush);

  return () => {
    clearExpectedNavigation();
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("submit", onSubmit, true);
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("pagehide", flush);
  };
}

export function isDocumentNavigationClick(event: MouseEvent): boolean {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return false;
  }

  const target = event.target;
  if (!(target instanceof Element)) return false;

  const anchor = target.closest("a[href]");
  if (!(anchor instanceof HTMLAnchorElement)) return false;
  if (anchor.hasAttribute("download")) return false;

  const href = anchor.getAttribute("href")?.trim() ?? "";
  if (!href || href.startsWith("#")) return false;
  if (/^(?:javascript|mailto|tel):/i.test(href)) return false;

  const linkTarget = anchor.getAttribute("target");
  return !linkTarget || linkTarget.toLowerCase() === "_self";
}

export function isDocumentNavigationSubmit(event: SubmitEvent): boolean {
  if (event.defaultPrevented) return false;

  const target = event.target;
  if (!(target instanceof Element)) return false;

  const form = target.closest("form");
  if (!(form instanceof HTMLFormElement)) return false;
  if (form.method.toLowerCase() === "dialog") return false;

  const formTarget = form.getAttribute("target");
  return !formTarget || formTarget.toLowerCase() === "_self";
}
